<template>
  <v-app v-bind:class="{ 'is-playing': isPlaying, 'is-battling': isPlaying && !!battle }">
    <div class="background"></div>
    <v-app-bar
      app
      flat
    >
      <v-btn icon :disabled="true" id="max-throws" v-bind:class="{ 'has-shadow': isPlaying && !battle }">
        <span v-show="isPlaying && !battle" class="gradient-text">{{ maxThrows === null ? 3 : maxThrows }}x</span>
      </v-btn>

      <v-spacer></v-spacer>
      <v-toolbar-title>{{ title }}</v-toolbar-title>
      <v-spacer></v-spacer>

      <v-menu bottom left>
        <template v-slot:activator="{ on }">
          <v-btn icon :disabled="!isPlaying" v-on="on">
            <v-icon v-show="isPlaying" class="gradient-text">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="isPlaying = false">
            <v-list-item-title>Quit game</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-content v-if="!isLoading">
      <v-scroll-y-transition>
        <player-select v-show="!isPlaying" :isPlaying="isPlaying" @start="setupGame"></player-select>
      </v-scroll-y-transition>
      <v-scroll-y-transition>
        <player-battle v-show="isPlaying && !!battle" :battle="battle" @finish="finishBattle"></player-battle>
      </v-scroll-y-transition>
      <v-scroll-y-transition>
        <div v-show="isPlaying && !battle" id="game" @click="throwDices">
          <div class="dices-container">
            <div class="dices" v-if="currentTurn.throws.length === 0">
              <dice></dice>
              <dice></dice>
            </div>
            <div class="dices" v-for="(value, index) in currentTurn.throws.slice().reverse()" :key="value.index">
              <dice :input="value.first" @clicked.stop="keepDice(value.first, index)"></dice>
              <dice :input="value.second" @clicked.stop="keepDice(value.second, index)"></dice>
            </div>
          </div>
          <div class="totals-container">
            <div class="current-total" v-bind:class="{ empty: currentTurn.throws.length === 0 && canThrow }">{{ currentTurn.throws.length > 0 ? currentTurn.throws[currentTurn.throws.length - 1].total : null }}</div>
            <div class="mex-total total">
              <span class="icon"></span>
              <span class="text">{{ mexCount }}</span>
            </div>
            <div class="lowest-total total">
              <span class="text">{{ lowestTotal ? lowestTotal : '?' }}</span>
              <span class="icon"></span>
            </div>
            <div class="knight">
              <span class="icon"></span>
              <span class="text">{{ knight ? knight.name : '?' }}</span>
            </div>
          </div>
        </div>
      </v-scroll-y-transition>
    </v-content>
    <v-fade-transition>
      <div class="next-dialog" v-show="isPlaying && finishedTurn && nextPlayer && !popupDialog" @click.stop="startNextPlayer">
        <div class="content" v-if="nextPlayer">
          <span class="title">Next up</span>
          <span class="text gradient-text">{{ nextPlayer.name }}</span>
        </div>
      </div>
    </v-fade-transition>
    <v-fade-transition>
      <div class="finished-dialog" v-show="isPlaying && finishedTurn && !nextPlayer && !popupDialog && loser">
        <div class="content" v-if="loser">
          <span class="title">Round finished</span>
          <span class="text gradient-text">{{ loser.name }}</span>
          <span class="text">Drink {{ Math.pow(2, mexCount + 1) }}</span>
          <v-btn class="button" @click.stop="restart"><v-icon left>mdi-dice-multiple</v-icon>Next round</v-btn>
        </div>
      </div>
    </v-fade-transition>
    <v-fade-transition>
      <div class="popup-dialog" v-show="isPlaying && popupDialog != null" @click.stop="popupDialog = null">
        <div class="content" v-if="popupDialog">
          <span class="title">{{ popupDialog.title }}</span>
          <span class="text" v-bind:class="{ 'gradient-text': index === 0 }" v-for="(line, index) in popupDialog.text" :key="index">{{ line }}</span>
        </div>
      </div>
    </v-fade-transition>
    <v-fade-transition>
      <div class="finished-dialog" v-show="!battle && isPlaying && finishedTurn && !nextPlayer && !popupDialog && !loser" @click.stop="startBattle">
        <div class="content">
          <span class="title">Battle time</span>
          <span class="text gradient-text">Prepare for battle!</span>
        </div>
      </div>
    </v-fade-transition>
    <v-snackbar
      v-model="$root.isNewVersionAvailable"
      :timeout="0"
    >
      New version available
      <v-btn
        color="purple"
        text
        @click="loadNewVersion"
      >
        Update
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import PlayerSelect from './components/PlayerSelect';
import PlayerBattle from './components/PlayerBattle';
import Dice from './components/Dice';

export default {
  name: 'App',
  components: {
    'player-select': PlayerSelect,
    'player-battle': PlayerBattle,
    'dice': Dice
  },
  data: () => ({
    players: [],
    isLoading: true,
    isPlaying: false,
    currentTurn: {
      player: null,
      throws: []
    },
    turns: [],
    battle: null,
    hasThrowTimeout: false,
    knight: null,
    popupDialog: null,
    loser: null
  }),
  watch: {
    players: {
      handler (newValue) { localStorage.players = JSON.stringify(newValue); },
      deep: true
    },
    isPlaying (newValue) { localStorage.isPlaying = newValue; },
    currentTurn: {
      handler (newValue) { localStorage.currentTurn = JSON.stringify(newValue); },
      deep: true
    },
    turns: {
      handler (newValue) { localStorage.turns = JSON.stringify(newValue); },
      deep: true
    },
    battle (newValue) { localStorage.battle = JSON.stringify(newValue); },
    knight: {
      handler (newValue) { localStorage.knight = JSON.stringify(newValue); },
      deep: true
    },
    popupDialog: {
      handler (newValue) { localStorage.popupDialog = JSON.stringify(newValue); },
      deep: true
    },
    loser: {
      handler (newValue) { localStorage.loser = JSON.stringify(newValue); },
      deep: true
    }
  },
  computed: {
    title () {
      if (!this.isPlaying) {
        return 'Select your players';
      }

      if (this.battle) {
        return '';
      }

      return this.currentPlayer ? this.currentPlayer.name : '';
    },
    currentPlayer () {
      return this.currentTurn.player;
    },
    maxThrows () {
      if (this.turns.length === 0) {
        return null;
      }

      return this.getThrowCount(this.turns[0].throws);
    },
    canThrow () {
      if (!this.isPlaying || this.battle || this.hasThrowTimeout) {
        return false;
      }

      return !this.finishedTurn;
    },
    finishedTurn () {
      let maxThrows = this.maxThrows;
      if (maxThrows === null) {
        maxThrows = 3;
      }

      if (this.currentTurn.throws.length > 0) {
        if (parseInt(this.lastThrow.total) === 32 || parseInt(this.lastThrow.total) === 21) {
          return true;
        }
      }

      return this.getThrowCount(this.currentTurn.throws) >= maxThrows;
    },
    nextPlayer () {
      if (!this.currentTurn.player || this.turns.length + 1 >= this.players.length) {
        return null;
      }

      let index = this.players.findIndex(player => player.id === this.currentTurn.player.id) + 1;

      if (index >= this.players.length) {
        index = 0;
      }

      return this.players[index];
    },
    lastThrow () {
      if (this.currentTurn.throws.length === 0) {
        return null;
      }

      return this.currentTurn.throws[this.currentTurn.throws.length - 1]
    },
    mexCount () {
      let mexCount = 0;

      this.turns.forEach(turn => {
        if (parseInt(turn.throws[turn.throws.length - 1].total) === 21) {
          mexCount++;
        }
      });

      if (this.nextPlayer && this.finishedTurn && this.lastThrow.total === 21) {
        mexCount++;
      }

      return mexCount;
    },
    lowestTotal () {
      let lowest = null;

      this.turns.forEach(turn => {
        var turnTotal = parseInt(turn.throws[turn.throws.length - 1].total);
        if (lowest === null || ((turnTotal < lowest || lowest == 21) && turnTotal != 21)) {
          lowest = turnTotal;
        }
      });

      if (this.finishedTurn && this.nextPlayer && (!lowest || ((this.lastThrow.total < lowest || lowest == 21) && this.lastThrow.total != 21))) {
        lowest = this.lastThrow.total;
      }

      return lowest;
    },
    lowestPlayers () {
      let lowestPlayers = [];
      let lowest = this.lowestTotal;

      this.turns.forEach(turn => {
        if (turn.throws[turn.throws.length - 1].total === lowest) {
          lowestPlayers.push(turn.player);
        }
      });

      return lowestPlayers;
    }
  },
  mounted () {
    this.initData();
    this.isLoading = false;
  },
  methods: {
    loadNewVersion() {
      this.$root.versionWorker.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload(true);
    },
    initData () {
      if (localStorage.players) this.players = JSON.parse(localStorage.players);
      if (localStorage.isPlaying) this.isPlaying = localStorage.isPlaying === 'true';
      if (localStorage.currentTurn) this.currentTurn = JSON.parse(localStorage.currentTurn);
      if (localStorage.turns) this.turns = JSON.parse(localStorage.turns);
      if (localStorage.battle) this.battle = JSON.parse(localStorage.battle);
      if (localStorage.knight) this.knight = JSON.parse(localStorage.knight);
      if (localStorage.popupDialog) this.popupDialog = JSON.parse(localStorage.popupDialog);
      if (localStorage.loser) this.loser = JSON.parse(localStorage.loser);
    },
    setupGame (players) {
      if (this.isPlaying) {
        return;
      }
      
      this.players = players;
      this.isPlaying = true;
      this.knight = null;
      this.restart();
      this.currentTurn.player = players[0];
    },
    restart () {
      this.currentTurn = {
        player: this.loser,
        throws: []
      };
      this.turns = [];
      this.loser = null;
      this.battle = null;
    },
    getThrowCount (throws) {
      let throwCount = throws.length;

      throws.forEach(t => {
        if (parseInt(t.total) === 31) {
          throwCount--;
        }
      });

      return throwCount;
    },
    getThrow (previousThrow) {
      let number1 = previousThrow && previousThrow.first.keep ? previousThrow.first.number : this.randomNumber();
      let number2 = previousThrow && previousThrow.second.keep ? previousThrow.second.number : this.randomNumber();

      return {
        total: parseInt(number1 > number2 ? (number1 + '' + number2) : (number1 === number2 ? (number1 * 100) : (number2 + '' + number1))),
        index: previousThrow ? previousThrow.index + 1 : 1,
        first: {
          number: number1,
          keep: false
        },
        second: {
          number: number2,
          keep: false
        }
      };
    },
    randomNumber () {
      return Math.floor((Math.random() * 6) + 1);
    },
    throwDices () {
      if (!this.canThrow) {
        return;
      }

      let t = this.getThrow(this.lastThrow);

      this.currentTurn.throws.push(t);

      this.hasThrowTimeout = true;
      setTimeout(() => {
        this.hasThrowTimeout = false;
      }, 500);

      if (t.total === 100) {
        this.knight = this.currentTurn.player;
      }

      if (t.total > 100 && this.knight) {
        if (this.knight.id === this.currentTurn.player.id) {
          this.popupDialog = {
            title: 'Someone is fucked',
            text: [
              this.knight.name,
              'Give ' + t.total / 100 + ' drinks'
            ]
          };
        } else {
          this.popupDialog = {
            title: 'The knight is fucked',
            text: [
              this.knight.name,
              'Drink ' + t.total / 100
            ]
          };
        }
      }

      if (t.total === 31) {
        this.popupDialog = {
          title: 'Share your success',
          text: [
            this.currentTurn.player.name,
            'Give 1 drink and throw again'
          ]
        };
      }

      if (this.finishedTurn && !this.nextPlayer) {
        this.turns.push(this.currentTurn);

        if (this.lowestPlayers.length === 1) {
          this.loser = this.lowestPlayers[0];
        }
      }
    },
    keepDice (dice, throwIndex) {
      if (!this.canThrow) {
        return;
      }

      if (throwIndex > 0) {
        this.throwDices();
        return;
      }

      let currentThrow = this.currentTurn.throws[this.currentTurn.throws.length - 1];
      if (currentThrow.total === 31) {
        return;
      }

      let keptBefore = false;
      this.currentTurn.throws.forEach(t => {
        if (t.index != this.lastThrow.index && (t.first.keep || t.second.keep)) {
          keptBefore = true;
        }
      });

      if (keptBefore) {
        return;
      }

      let originalKeep = dice.keep;

      currentThrow.first.keep = false;
      currentThrow.second.keep = false;

      dice.keep = !originalKeep;
    },
    startNextPlayer () {
      if (!this.nextPlayer || !this.finishedTurn) {
        return;
      }

      let currentTurn = this.currentTurn;

      this.currentTurn = {
        player: this.nextPlayer,
        throws: []
      };

      this.turns.push(currentTurn);
    },
    finishBattle (loser) {
      this.loser = loser;
      this.battle = null;
    },
    startBattle () {
      const shuffledPlayers = this.lowestPlayers.sort(() => 0.5 - Math.random());
      this.battle = {
        firstPlayer: shuffledPlayers[0],
        secondPlayer: shuffledPlayers[1]
      };
    }
  }
};
</script>
